import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SCHEDULED_REPORT_TYPE_LABELS,
  scheduleSummary,
  type ScheduledReportType,
  type ScheduleFrequency,
  type ScheduleFormat,
} from '@cls/shared';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { useAuth } from '../features/auth/AuthProvider.js';
import { ScheduledReportModal } from '../components/domain/ScheduledReportModal.js';

interface ScheduledReport {
  id: string;
  name: string;
  reportType: ScheduledReportType;
  schedule: ScheduleFrequency;
  weekday: number | null;
  dayOfMonth: number | null;
  format: ScheduleFormat;
  time: string;
  recipients: string[];
  enabled: boolean;
  createdBy: string | null;
  lastRunAt: string | null;
  nextRunAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
}

export function ScheduledReports() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduledReport | null>(null);

  const canManage = !!user && user.role === 'Admin';

  const { data: reports = [] } = useQuery({
    queryKey: ['scheduled-reports'],
    queryFn: () => api.get<ScheduledReport[]>('/scheduled-reports').then((r) => r.data),
  });

  const toggle = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch(`/scheduled-reports/${id}`, { enabled }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['scheduled-reports'] });
      toast.info(vars.enabled ? 'Enabled' : 'Disabled', vars.enabled ? 'Schedule activated' : 'Schedule paused');
    },
    onError: () => toast.error('Toggle failed'),
  });

  const runNow = useMutation({
    mutationFn: (id: string) =>
      api.post<{ status: string; lastError: string | null }>(`/scheduled-reports/${id}/run`, {}).then((r) => r.data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['scheduled-reports'] });
      if (res.status === 'ok') toast.success('Report sent', 'Delivered to all recipients');
      else if (res.status === 'partial') toast.error('Partly sent', res.lastError ?? 'Some recipients failed');
      else toast.error('Send failed', res.lastError ?? 'See the schedule card for details');
    },
    onError: () => toast.error('Run failed', 'Could not reach the server'),
  });

  const del = useMutation({
    mutationFn: async (id: string) => api.delete(`/scheduled-reports/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scheduled-reports'] });
      toast.info('Schedule deleted');
    },
    onError: () => toast.error('Delete failed'),
  });

  if (!canManage) {
    return (
      <div className="page" id="page-scheduledreports">
        <div className="hbar">
          <div>
            <div className="ph">📨 Scheduled Reports</div>
            <div className="ps">Configure automated report delivery</div>
          </div>
        </div>
        <div id="sched-role-gate" className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
          🔒 Access Restricted — Admin role required
        </div>
      </div>
    );
  }

  return (
    <div className="page" id="page-scheduledreports">
      <div className="hbar">
        <div>
          <div className="ph">📨 Scheduled Reports</div>
          <div className="ps">Configure automated report delivery</div>
        </div>
        <div style={{ display: 'flex', gap: 7 }} id="sched-add-btn">
          <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)}>+ Add Schedule</button>
        </div>
      </div>

      <div id="sched-body">
        {reports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>⏰</div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>No Scheduled Reports</div>
            <div style={{ fontSize: 12, marginBottom: 14 }}>Set up automated report delivery to keep your team informed.</div>
            <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)}>+ Add Your First Schedule</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {reports.map((r) => (
              <ScheduleCard
                key={r.id}
                r={r}
                onToggle={(enabled) => toggle.mutate({ id: r.id, enabled })}
                onEdit={() => setEditing(r)}
                onDelete={() => { if (confirm(`Delete "${SCHEDULED_REPORT_TYPE_LABELS[r.reportType] ?? r.reportType}"?`)) del.mutate(r.id); }}
                onRun={() => runNow.mutate(r.id)}
                running={runNow.isPending && runNow.variables === r.id}
              />
            ))}
          </div>
        )}
      </div>

      {addOpen ? <ScheduledReportModal onClose={() => setAddOpen(false)} /> : null}
      {editing ? (
        <ScheduledReportModal
          onClose={() => setEditing(null)}
          initial={{
            id: editing.id,
            reportType: editing.reportType,
            schedule: editing.schedule,
            weekday: editing.weekday,
            dayOfMonth: editing.dayOfMonth,
            time: editing.time,
            format: editing.format,
            recipients: editing.recipients,
            enabled: editing.enabled,
          }}
        />
      ) : null}
    </div>
  );
}

interface ScheduleCardProps {
  r: ScheduledReport;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  onRun: () => void;
  running: boolean;
}

function ScheduleCard({ r, onToggle, onEdit, onDelete, onRun, running }: ScheduleCardProps) {
  const reportLabel = SCHEDULED_REPORT_TYPE_LABELS[r.reportType] ?? r.reportType;
  const summary = scheduleSummary(r.schedule, r.weekday, r.dayOfMonth, r.time);
  const recipientLine = r.recipients.length ? r.recipients.join(', ') : 'No recipients set';
  const lastRunStr = r.lastRunAt ? new Date(r.lastRunAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
  const nextRunStr = r.nextRunAt ? new Date(r.nextRunAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
  return (
    <div style={{
      background: 'var(--card)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      padding: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      flexWrap: 'wrap',
      opacity: r.enabled ? 1 : 0.65,
    }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 4 }}>
          {reportLabel}
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{`⏰ ${summary}`}</div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{`✉ ${recipientLine}`}</div>
        <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 2 }}>
          {`Format: ${r.format.toUpperCase()}${r.createdBy ? ` · Created by ${r.createdBy}` : ''} · Next: ${nextRunStr}`}
        </div>
        {running ? (
          <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 3 }}>⟳ Running…</div>
        ) : r.lastStatus === 'ok' ? (
          <div style={{ fontSize: 10, color: 'var(--live)', marginTop: 3 }}>{`✓ Successfully sent · ${lastRunStr}`}</div>
        ) : r.lastStatus === 'partial' ? (
          <div style={{ fontSize: 10, color: 'var(--warn)', marginTop: 3 }}>{`⚠ Partly sent · ${lastRunStr}${r.lastError ? ` — ${r.lastError}` : ''}`}</div>
        ) : r.lastStatus === 'failed' ? (
          <div style={{ fontSize: 10, color: 'var(--danger)', marginTop: 3 }}>{`✗ Failed · ${lastRunStr}${r.lastError ? ` — ${r.lastError}` : ''}`}</div>
        ) : (
          <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 3 }}>Not yet run</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11, color: 'var(--muted)' }}>
          <input
            type="checkbox"
            checked={r.enabled}
            onChange={(e) => onToggle(e.target.checked)}
            style={{ accentColor: 'var(--accent)' }}
          />
          Active
        </label>
        <button className="btn btn-ghost btn-sm" onClick={onRun} disabled={running} style={{ fontSize: 11 }} title="Run now">{running ? '⟳ Running…' : '▶ Run'}</button>
        <button className="btn btn-ghost btn-sm" onClick={onEdit} style={{ fontSize: 11 }}>✎ Edit</button>
        <button className="btn btn-ghost btn-sm" onClick={onDelete} style={{ fontSize: 11, color: 'var(--danger)' }}>✕ Delete</button>
      </div>
    </div>
  );
}
